<template>
  <main role="main" class="py-4">
    <b-container>

      <div v-if="isLoading" class="py-5 text-center"><b-spinner /></div>

      <template v-else-if="meeting">
        <div class="meeting-header-modern">
          <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between style-gap-md">
            <div>
              <h4 class="m-0 font-weight-bold text-dark break-word-max mb-2">{{ meeting.name }}</h4>
              <div class="d-flex flex-wrap align-items-center text-secondary" style="gap: 12px; font-size: 14.5px;">
                <b-badge :variant="statusBadgeVariant[meeting.status]" class="px-2 py-1" style="font-size: 12px; border-radius: 6px; font-weight: 600;">
                  {{ statusText[meeting.status] }}
                </b-badge>
                <div><b-icon-tag class="mr-1" /> ID: <span class="text-dark font-weight-medium">{{ meeting.id }}</span></div>
                <Badge :meeting="meeting" />
                <div v-if="isUserAdmin()">
                  <b-icon-person class="mr-1" /> {{ $t("customer") }}:
                  <router-link :to="`/customers/details/` + meeting.customerId" class="text-primary font-weight-medium">
                    {{ meeting.customerName || meeting.customerId }}
                  </router-link>
                </div>
                <b-button v-if="!isViewUser() && [enumMeetingStatus.INPROGRESS, enumMeetingStatus.END].includes(meeting.status)"
                  variant="outline-primary" size="sm" class="btn-circle-monitor" :to="`/monitor-meeting/${meeting.id}`"
                  v-b-tooltip.hover :title="$t('viewStats')">
                  <b-icon-graph-up></b-icon-graph-up>
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex flex-wrap align-items-center justify-content-between mt-2" style="gap: 16px;">
          <!-- Left side actions: Start, Join, End, Recording -->
          <div class="d-flex flex-wrap align-items-center" style="gap: 12px;">
            <b-button variant="outline-primary" size="sm"
              v-if="isOwnerOrHost && [enumMeetingStatus.PENDING,enumMeetingStatus.END].includes(meeting.status)" @click="onClickStartMeeting">
              <b-icon icon="display" class="mr-1"></b-icon> {{ $t("start") }}
            </b-button>

            <template v-if="meeting.status === enumMeetingStatus.INPROGRESS">
              <b-button v-if="(isOwnerOrHost || meeting.canJoin) && !isUserAdmin() && !isUserSuperAdmin()" variant="outline-primary" size="sm"
                @click="onClickJoinMeeting">
                <b-icon icon="display" class="mr-1"></b-icon> {{ $t("joinMeeting") }}
              </b-button>
              <b-button v-if="isOwnerOrHost" variant="outline-danger" size="sm" @click="onClickEndMeeting">
                <b-icon icon="power" class="mr-1"></b-icon> {{ $t("endMeeting") }}
              </b-button>
            </template>

            <!-- Recording Actions -->
            <template v-if="meeting.status === 'END' && (isOwnerOrHost || isUserAdmin())">
              <b-spinner v-if="isLoadingRecordUrl" small />

              <template v-if="meeting.usedRecording === 1 && (meeting.recordingSize > 0 || meeting.recordingMp3Size > 0)">
                <div class="d-inline-block position-relative">
                  <b-button variant="outline-primary" size="sm" class="btn-recording-toggle" @mouseenter="openRecordingPopup" @click="openRecordingPopup">
                    <template v-if="recordingLinks && recordingLinks.mp4 && recordingLinks.mp4.playbackUrl">
                      <b-icon icon="camera-video" class="mr-1" />
                      {{ $t('viewRecordingVideoWithSize', { size: recordingLinks.mp4.sizeFormatted || convertToMB(meeting.recordingSize) + 'MB' }) }}
                    </template>
                    <template v-else>
                      <b-icon icon="play-circle" class="mr-1" />
                      {{ $t('viewRecordingAudioWithSize', { size: recordingLinks.mp3.sizeFormatted || convertToMB(meeting.recordingMp3Size) + 'MB' }) }}
                    </template>
                  </b-button>

                  <div v-if="showRecordingPopup" class="recording-popup shadow">
                    <b-icon-x class="close-popup-icon" @click="closeRecordingPopup" />
                    <h6 class="popup-title">{{ $t("recordingContent") }}</h6>
                    <template v-if="recordingLinks">
                      <!-- MP3 Item -->
                      <div v-if="recordingLinks.mp3 && recordingLinks.mp3.playbackUrl" class="recording-item mp3">
                        <h6 class="header-record">{{ $t('recordingAudioOnly') }}</h6>
                        <div class="recording-link" @click="openRecording(recordingLinks.mp3.playbackUrl)">{{ recordingLinks.mp3.playbackUrl }}</div>
                        <div class="recording-info">
                          <span class="pill-size text-secondary" v-b-tooltip.hover="{ customClass: 'tooltip-class', title: $t('totalRecordingSize') }">
                            <b-icon-hdd class="mr-1" /> {{ recordingLinks.mp3.sizeFormatted || '-' }}
                          </span>
                          <div class="recording-actions">
                            <b-icon-files class="action-icon icon-copy" v-b-tooltip.hover="{ customClass: 'tooltip-class', title: $t('copy') }" @click="copyText(recordingLinks.mp3.playbackUrl)" />
                            <b-icon-play class="action-icon play-class icon-view" v-b-tooltip.hover="{ customClass: 'tooltip-class', title: $t('view') }" @click="openRecording(recordingLinks.mp3.playbackUrl)" />
                            <b-icon-download class="action-icon icon-download" v-b-tooltip.hover="{ customClass: 'tooltip-class', title: $t('download') }" @click="downloadRecording(recordingLinks.mp3.playbackUrl, meeting.id + '_MP3_' + meeting.name + '.mp3')" />
                            <b-icon-trash v-if="isOwnerOrHost" class="action-icon icon-delete" v-b-tooltip.hover="{ customClass: 'tooltip-class', title: $t('delete') }" @click="onClickDeleteRecordingFile('mp3')" />
                          </div>
                        </div>
                      </div>

                      <div class="mt-2-c" v-if="recordingLinks.mp3 && recordingLinks.mp3.playbackUrl && recordingLinks.mp4 && recordingLinks.mp4.playbackUrl"></div>

                      <!-- MP4 Item -->
                      <div v-if="recordingLinks.mp4 && recordingLinks.mp4.playbackUrl" class="recording-item mp4">
                        <h6 class="header-record">{{ $t('recordingVideo') }}</h6>
                        <div class="recording-link" @click="openRecording(recordingLinks.mp4.playbackUrl)">{{ recordingLinks.mp4.playbackUrl }}</div>
                        <div class="recording-info">
                          <span class="pill-size text-secondary" v-b-tooltip.hover="{ customClass: 'tooltip-class', title: $t('totalRecordingSize') }">
                            <b-icon-hdd class="mr-1" /> {{ recordingLinks.mp4.sizeFormatted || '-' }}
                          </span>
                          <div class="recording-actions">
                            <b-icon-files class="action-icon icon-copy" v-b-tooltip.hover="{ customClass: 'tooltip-class', title: $t('copy') }" @click="copyText(recordingLinks.mp4.playbackUrl)" />
                            <b-icon-play class="action-icon play-class icon-view" v-b-tooltip.hover="{ customClass: 'tooltip-class', title: $t('view') }" @click="openRecording(recordingLinks.mp4.playbackUrl)" />
                            <b-icon-download class="action-icon icon-download" v-b-tooltip.hover="{ customClass: 'tooltip-class', title: $t('download') }" @click="downloadRecording(recordingLinks.mp4.playbackUrl, meeting.id + '_MP4_' + meeting.name + '.mp4')" />
                            <b-icon-trash v-if="isOwnerOrHost" class="action-icon icon-delete" v-b-tooltip.hover="{ customClass: 'tooltip-class', title: $t('delete') }" @click="onClickDeleteRecordingFile('mp4')" />
                          </div>
                        </div>
                      </div>

                      <div v-if="!(recordingLinks.mp3 && recordingLinks.mp3.playbackUrl) && !(recordingLinks.mp4 && recordingLinks.mp4.playbackUrl)" class="text-secondary small py-2">
                        {{ $t("meetingNoRecordedFile") }}
                      </div>
                    </template>
                    <div v-else class="text-center py-2">
                      <b-spinner small />
                    </div>
                  </div>
                </div>
              </template>

              <span v-else-if="meeting.isRecorded === 0 && meeting.usedRecording === 1" class="recording-progress d-flex align-items-center">
                <b-icon icon="arrow-repeat" class="mr-1 spin-icon" style="fill: #63C454; font-size: 20px;" />
                {{ $t("meetingProgressRecorded") }}
              </span>
              <span v-else-if="meeting.deletedRecordingAt && !meeting.recordingSize && !meeting.recordingMp3Size" class="text-secondary d-flex align-items-center" style="font-size: 14.5px;">
                <b-icon icon="slash-circle" class="mr-1" /> {{ $t("recodingDeleted") }}
              </span>
              <span v-else class="text-secondary d-flex align-items-center" style="font-size: 14.5px;">
                <b-icon icon="slash-circle" class="mr-1" /> {{ $t("meetingNoRecordedFile") }}
              </span>
            </template>
          </div>

          <!-- Right side actions: Manage, clone, edit, delete, export -->
          <div class="d-flex flex-wrap align-items-center" style="gap: 12px;">
            <template v-if="isOwnerOrHost">
              <b-button variant="outline-secondary" size="sm" :to="`/meetings/new?cloneFrom=${meetingId}`">
                <b-icon-files class="mr-1" /> {{ $t("clone") }}
              </b-button>

              <b-button v-if="meeting.status == enumMeetingStatus.END && peers.length != 0" variant="outline-primary" size="sm" @click="downloadFileParticipant">
                <b-icon-download class="mr-1" /> {{ $t("exportParticipants") }}
              </b-button>

              <b-button v-if="meeting.status === 'PENDING'" variant="outline-primary" size="sm" :to="`/meetings/edit/${meetingId}`">
                <b-icon-pencil-square class="mr-1" /> {{ $t("updateInfo") }}
              </b-button>

              <!-- Dropdown for destructive options -->
              <b-dropdown v-if="meeting.status !== 'INPROGRESS' || (meeting.isRecorded === 1 && meeting.recordingSize > 0)" right variant="outline-danger" size="sm" toggle-class="d-flex align-items-center" no-caret>
                <template v-slot:button-content>
                  <b-icon-three-dots class="mr-1" /> {{ $t("more") }}
                </template>

                <b-dropdown-item v-if="meeting.status === enumMeetingStatus.PENDING" @click="onClickCancelMeeting" class="text-danger">
                  <b-icon-slash-circle class="mr-1 text-danger" /> {{ $t("cancelMeeting") }}
                </b-dropdown-item>

                <b-dropdown-item v-if="canDeleteRecording" @click="onClickDeleteRecording" class="text-danger">
                  <b-icon-trash class="mr-1 text-danger" /> {{ $t("deleteRecording") }}
                </b-dropdown-item>

                <b-dropdown-item v-if="meeting.status !== 'INPROGRESS'" @click="onClickDeleteMeeting" class="text-danger">
                  <b-icon-trash class="mr-1 text-danger" /> {{ $t("deleteMeeting") }}
                </b-dropdown-item>
              </b-dropdown>
            </template>

            <template v-else-if="isUserAdmin()">
              <b-button v-if="meeting.status == enumMeetingStatus.END && peers.length != 0" variant="outline-primary" size="sm" @click="downloadFileParticipant">
                <b-icon-download class="mr-1" /> {{ $t("exportParticipants") }}
              </b-button>
            </template>
          </div>
        </div>

        <b-alert v-if="emailStatusAlertText && isOwnerOrHost && !isUserAdmin() && !isUserSuperAdmin()" show :variant="emailStatusAlertVariant"
          class="custom-alert">
          <div class="d-flex align-items-center" style="gap: 10px;">
            <b-icon-exclamation-circle class="alert-icon" />
            <span>{{ emailStatusAlertText }}</span>
          </div>
          <b-button v-if="isOwnerOrHost && emailStatusButtonText" variant="outline-primary" size="sm" class="btn-alert-action"
            @click="onClickEmailStatusAlertButton">
            <b-icon-envelope class="mr-1" /> {{ emailStatusButtonText }}
          </b-button>
        </b-alert>

        <hr>

        <b-row id="conf-info-2">
          <b-col cols="6" class="border-right pr-5">
            <!-- Card 1: Meeting Info -->
            <div class="meta-card">
              <h5 class="section-title mb-3 d-flex align-items-center" style="gap: 8px;">
                <b-icon-info-circle /> {{ $t("meetingInfo") }}
              </h5>

              <div class="meta-row">
                <div class="meta-label">
                  <b-icon-calendar-event /> {{ $t("meetingDate") }}
                </div>
                <div class="meta-value">{{ localDateFormat(meeting.scheduleStartTime) }}</div>
              </div>

              <div class="meta-row">
                <div class="meta-label">
                  <b-icon-clock /> {{ $t("meetingScheduledTime") }}
                </div>
                <div class="meta-value">
                  <div v-if="localDateFormat(meeting.scheduleStartTime) === localDateFormat(meeting.scheduleEndTime)">
                    {{ localTimeFormat(meeting.scheduleStartTime) }} - {{ localTimeFormat(meeting.scheduleEndTime) }}
                  </div>
                  <div v-else>
                    {{ $t('from') }} {{ localTimeFormat(meeting.scheduleStartTime) }} {{ $t('day') }} {{ localDateFormat(meeting.scheduleStartTime) }}
                    <br/>{{ $t('to') }} {{ localTimeFormat(meeting.scheduleEndTime) }} {{ $t('day') }} {{ localDateFormat(meeting.scheduleEndTime) }}
                  </div>
                </div>
              </div>

              <div v-if="meeting.status !== enumMeetingStatus.PENDING" class="meta-row">
                <div class="meta-label">
                  <b-icon-hourglass-split /> {{ $t("meetingActualTime") }}
                </div>
                <div class="meta-value" :style="{ whiteSpace: 'pre-line' }">{{ actualTimeStr }}</div>
              </div>

              <div v-if="meeting.status === enumMeetingStatus.END" class="meta-row">
                <div class="meta-label">
                  <b-icon-stopwatch /> {{ $t("meetingDuration") }}
                </div>
                <div class="meta-value">{{ formatDurationFromRange() }}</div>
              </div>

              <div class="meta-row">
                <div class="meta-label">
                  <b-icon-people /> {{ $t("numOfParticipants") }}
                </div>
                <div class="meta-value">
                  {{ meeting.numOfParticipants }}
                  <b-button v-if="canEditMaxparticipant && isUserAdmin()" variant="outline-primary" style="border:0px solid #fff!important;background-color:transparent!important;padding:0px" size="sm" v-b-modal:edit-max-participant-modal>
                    <i class="fa fa-edit text-muted ml-1"></i>
                  </b-button>
                </div>
              </div>

              <div v-if="meeting.numUsers > 0" class="meta-row">
                <div class="meta-label">
                  <b-icon-person-check /> {{ $t("numOfActualParticipants") }}
                </div>
                <div class="meta-value">{{ meeting.numUsers }}</div>
              </div>

              <div v-if="isUserAdmin() || meeting.maxCam != null" class="meta-row">
                <div class="meta-label">
                  <b-icon-camera-video /> {{ $t("maxCamEnabled") }}
                </div>
                <div class="meta-value">
                  {{ getMaxCam }}
                  <b-button v-if="canEditMaxCam && isUserAdmin()" variant="outline-primary" style="border:0px solid #fff!important;background-color:transparent!important;padding:0px" size="sm" v-b-modal:edit-max-cam-modal>
                    <i class="fa fa-edit text-muted ml-1"></i>
                  </b-button>
                </div>
              </div>

              <div v-if="isWebinar && meeting.webinarThreshold != 0" class="meta-row">
                <div class="meta-label">
                  <b-icon-person-badge /> {{ $t("amountOfWebinarParticipants") }}
                </div>
                <div class="meta-value text-monospace">{{ meeting.webinarThreshold }}</div>
              </div>

              <div class="meta-row">
                <div class="meta-label">
                  <b-icon-shield-lock /> {{ $t("meetingType") }}
                </div>
                <div class="meta-value">{{ meeting.publicMeeting ? $t("public") : $t("private") }}</div>
              </div>

              <div v-if="checkAuthFaceIdFeature()" class="meta-row">
                <div class="meta-label">
                  <b-icon-eye /> {{ $t("joinByFaceId") }}
                </div>
                <div class="meta-value">
                  <b-icon-check-circle class="text-success" />
                </div>
              </div>

              <div v-if="isUserAdmin()" class="meta-row">
                <div class="meta-label">
                  <b-icon-person /> {{ $t("createBy") }}
                </div>
                <div class="meta-value">{{ meeting.createdBy }}</div>
              </div>

              <div v-if="isUserAdmin() && coreDomainTxt" class="meta-row">
                <div class="meta-label">
                  <b-icon-globe /> {{ $t("coreDomain") }}
                </div>
                <div class="meta-value">{{ coreDomainTxt }}</div>
              </div>
            </div>

            <!-- Card 2: Links & Access Codes -->
            <div class="meta-card">
              <h5 class="section-title mb-3 d-flex align-items-center" style="gap: 8px;">
                <b-icon-link-45deg /> {{ $t("linksAndAccessCodes") }}
              </h5>

              <div v-if="meeting.hostJoinUrl" class="link-card-modern host-card">
                <div class="link-card-info">
                  <div class="link-card-label">
                    <b-icon-shield /> {{ $t("hostJoinUrl") }}
                  </div>
                  <div class="link-card-url">
                    <a :href="meeting.hostJoinUrl" target="_blank" v-b-tooltip.hover :title="meeting.hostJoinUrl">{{ meeting.hostJoinUrl }}</a>
                  </div>
                </div>
                <div class="link-card-action">
                  <b-button class="btn-copy-modern" @click="onClickCopyHostJoinUrl" v-b-tooltip.hover :title="$t('copy')">
                    <b-icon-files />
                  </b-button>
                </div>
              </div>

              <div v-if="meeting.participantJoinUrl" class="link-card-modern participant-card">
                <div class="link-card-info">
                  <div class="link-card-label">
                    <b-icon-people /> {{ $t("participantJoinUrl") }}
                  </div>
                  <div class="link-card-url">
                    <a :href="meeting.participantJoinUrl" target="_blank" v-b-tooltip.hover :title="meeting.participantJoinUrl">{{ meeting.participantJoinUrl }}</a>
                  </div>
                </div>
                <div class="link-card-action">
                  <b-button class="btn-copy-modern" @click="onClickCopyJoinUrl" v-b-tooltip.hover :title="$t('copy')">
                    <b-icon-files />
                  </b-button>
                </div>
              </div>
              <div v-if="meeting.participantJoinUrl" class="d-flex justify-content-end align-items-center mt-3" style="gap: 8px;">
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="d-flex align-items-center btn-other-info"
                  style="gap: 6px; font-size: 13px; border-radius: 8px;"
                  @click="onClickOpenQRCodeModal"
                >
                  <img :src="publicPath + 'images/qrcode.png'" alt="QR Code" style="width: 16px; height: 16px; vertical-align: middle;" /> {{ $t("otherInfo") }}
                </b-button>
                <b-button
                  v-if="meeting.publicMeeting"
                  size="sm"
                  variant="outline-primary"
                  class="d-flex align-items-center"
                  style="gap: 6px; font-size: 13px; border-radius: 8px;"
                  @click="onClickCopyJoinUrlAndAccessCode"
                >
                  <b-icon-files /> {{ $t("copy") }}
                </b-button>
              </div>
            </div>

            <!-- Card 3: Meeting Config -->
            <div v-if="meetingFeatures && meetingFeatures.length" class="meta-card">
              <h5 class="section-title mb-3 d-flex align-items-center" style="gap: 8px;">
                <b-icon-gear /> {{ $t("meetingConfig") }}
              </h5>
              <fieldset readonly>
                <div class="mb-2" v-for="meetingFeature in meetingFeatures" :key="meetingFeature.featureId">
                  <div class="d-flex align-items-center flex-wrap" style="gap: 10px;">
                    <read-only-check-box :checked="meetingFeature.isActive" />
                    <span style="font-size: 14px;">{{ meetingFeature.featureName }}</span>
                    <!-- Nếu là feature bảng trắng và có bảng trắng được chọn -->
                    <div v-if="meetingFeature.featureCode === 'ALLOW_WHITEBOARD' && meetingFeature.isActive && selectedWhiteboards.length > 0" class="d-inline-flex flex-wrap align-items-center" style="gap: 8px;">
                      <div v-for="wb in selectedWhiteboards" :key="wb.id" class="selected-whiteboard-pill d-inline-flex align-items-center">
                        <div class="pill-info-section" @click="openWhiteboardLink(wb.accessLink)">
                          <img :src="publicPath + 'images/whiteboard.svg'" alt="Whiteboard" style="width: 18px; height: 18px; vertical-align: middle; margin-right: 4px;" /><span class="whiteboard-link-text" v-b-tooltip.hover :title="wb.name">{{ wb.name }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
          </b-col>

          <b-col cols="6" class="pl-lg-4 pl-3" style="overflow: auto;">
            <template v-if="meeting.publicMeeting && meeting.status === enumMeetingStatus.INPROGRESS">
              <h5 class="section-title mb-4">{{ $t("meetingParticipantsJoined", { count: meeting.participantsJoined.length }) }}</h5>
              <b-list-group class="mb-4">
                <b-list-group-item v-for="(meetingParticipant, index) in meeting.participantsJoined"
                  :key="meetingParticipant.name + '-' + index" class="participant-item">
                  <div class="participant-name">
                    {{ meetingParticipant.name }}
                    <b-badge v-if="['OPERATOR', 'OPERATER'].includes(meetingParticipant.role)" variant="light"
                      class="ml-2 badge-operator">
                      {{ $t("meetingOperatorBadge") }}
                    </b-badge>
                  </div>
                </b-list-group-item>
              </b-list-group>
            </template>
            <template v-if="!meeting.publicMeeting && meeting.status === enumMeetingStatus.INPROGRESS">
              <b-form-radio-group class="custom-radio-group mb-3" size="sm" buttons button-variant="outline-primary"
                v-model="inProgressMeetingParticipantsViewMode"
                :options="inProgressMeetingParticipantsViewModeOptions" />
              <b-list-group class="mb-4">
                <b-list-group-item v-for="meetingParticipant in showParticipantsJoined"
                  :key="meetingParticipant.id || meetingParticipant.name" class="d-flex flex-row participant-item">
                  <div class="flex-grow-1">
                    <div class="participant-name">
                      {{ meetingParticipant.name }}
                      <b-badge v-if="['OPERATOR', 'OPERATER'].includes(meetingParticipant.role)" variant="light"
                        class="ml-2 badge-operator">
                        {{ $t("meetingOperatorBadge") }}
                      </b-badge>
                    </div>
                    <b-row v-if="meetingParticipant.email || meetingParticipant.phone || meetingParticipant.organization" class="mt-1">
                      <b-col cols="7" v-if="meetingParticipant.email" class="participant-detail text-truncate">
                        <b-icon class="mr-1" icon="envelope" />
                        {{ meetingParticipant.email }}
                      </b-col>
                      <b-col cols="5" v-if="meetingParticipant.phone" class="participant-detail">
                        <b-icon class="mr-1" icon="telephone" />
                        {{ meetingParticipant.phone }}
                      </b-col>
                      <b-col cols="12" v-if="meetingParticipant.organization" class="participant-detail mt-1">
                        <b-icon class="mr-1" icon="building" />
                        {{ meetingParticipant.organization }}
                      </b-col>
                    </b-row>
                  </div>
                  <div class="align-self-center">
                    <b-dropdown v-if="meetingParticipant.id && canChangeParticipants" variant="link"
                      toggle-class="text-decoration-none p-0 text-secondary" no-caret right menu-class="shadow">
                      <template v-slot:button-content>
                        <b-icon-three-dots-vertical />
                      </template>
                      <b-dropdown-item-button @click="onClickSendSingleEmailInvitation(meetingParticipant)">
                        {{ $t("sendEmailInvitation") }}
                      </b-dropdown-item-button>
                      <b-dropdown-item-button v-if="meetingParticipant.phone"
                        @click="onClickSendSingleSmsInvitation(meetingParticipant)">
                        {{ $t("sendSmsInvitation") }}
                      </b-dropdown-item-button>
                      <b-dropdown-item-button @click="onClickUpdateParticipant(meetingParticipant)">
                        {{ $t("update") }}
                      </b-dropdown-item-button>
                      <b-dropdown-item-button v-if="meetingParticipant.privateJoinUrl"
                        @click="onClickCopyPrivateJoinUrl(meetingParticipant)">
                        {{ $t("copyJoinUrl") }}
                      </b-dropdown-item-button>
                    </b-dropdown>
                  </div>
                </b-list-group-item>
              </b-list-group>
            </template>

            <!-- Tiêu đề -->
            <div class="d-flex flex-row align-items-center justify-content-between mb-3">
              <h5 class="section-title m-0">
                {{ $t("meetingParticipantsList") }} ({{ peers.length ? peers.length : meeting.meetingParticipants.length }})
              </h5>
              <div class="d-flex align-items-center" style="gap: 8px;">
                <b-button v-if="meeting.status == enumMeetingStatus.END && peers.length != 0" variant="outline-primary" class="ml-4"
                  size="sm" @click="downloadFileHistoryHandParticipant">
                  <b-icon-file-earmark-text class="mr-1" /> {{ $t("exportLog") }}
                </b-button>
                <b-button v-if="canAddParticipants" variant="outline-primary" size="sm" v-b-modal:new-participant-modal>
                  <b-icon-plus /> {{ $t("addMeetingParticipant") }}
                </b-button>
              </div>
            </div>

            <!-- Khu vực danh sách người tham gia -->
            <b-list-group style="display:block;">
              <div v-if="!peers.length">
                <b-list-group-item v-for="meetingParticipant in meeting.meetingParticipants"
                  :key="meetingParticipant.id" class="d-flex flex-row participant-item">
                  <div class="flex-grow-1">
                    <div class="participant-name">
                      {{ meetingParticipant.name }}
                      <b-badge v-if="meetingParticipant.role === 'OPERATOR'" variant="light" class="ml-2 badge-operator">
                        {{ $t("meetingOperatorBadge") }}
                      </b-badge>
                    </div>
                    <b-row v-if="meetingParticipant.email || meetingParticipant.phone || meetingParticipant.organization" class="mt-1">
                      <b-col cols="7" v-if="meetingParticipant.email" class="participant-detail text-truncate">
                        <b-icon class="mr-1" icon="envelope" />
                        {{ meetingParticipant.email }}
                      </b-col>
                      <b-col cols="5" v-if="meetingParticipant.phone" class="participant-detail">
                        <b-icon class="mr-1" icon="telephone" />
                        {{ meetingParticipant.phone }}
                      </b-col>
                      <b-col cols="12" v-if="meetingParticipant.organization" class="participant-detail mt-1">
                        <b-icon class="mr-1" icon="building" />
                        {{ meetingParticipant.organization }}
                      </b-col>
                    </b-row>
                  </div>
                  <div class="align-self-center">
                    <b-dropdown v-if="canChangeParticipants" variant="link" toggle-class="text-decoration-none p-0 text-secondary"
                      no-caret right menu-class="shadow">
                      <template v-slot:button-content>
                        <b-icon-three-dots-vertical />
                      </template>
                      <b-dropdown-item-button @click="onClickSendSingleEmailInvitation(meetingParticipant)">
                        {{ $t("sendEmailInvitation") }}
                      </b-dropdown-item-button>
                      <b-dropdown-item-button v-if="meetingParticipant.phone"
                        @click="onClickSendSingleSmsInvitation(meetingParticipant)">
                        {{ $t("sendSmsInvitation") }}
                      </b-dropdown-item-button>
                      <b-dropdown-item-button @click="onClickUpdateParticipant(meetingParticipant)">
                        {{ $t("update") }}
                      </b-dropdown-item-button>
                      <b-dropdown-item-button v-if="meetingParticipant.privateJoinUrl"
                        @click="onClickCopyPrivateJoinUrl(meetingParticipant)">
                        {{ $t("copyJoinUrl") }}
                      </b-dropdown-item-button>
                    </b-dropdown>
                  </div>
                </b-list-group-item>
              </div>
              <div v-if="peers.length"
                :style="(meeting.status === enumMeetingStatus.END && summaryArry != null && summaryArry.toString().trim() !== '')
                  ? 'max-height: 40vh; overflow-y: auto; margin-bottom: calc(1rem - 5px);'
                  : 'max-height: 100vh; overflow-y: auto; margin-bottom: 0;'"
              >
                <b-list-group-item v-for="peer in (showAll ? peers : peers.slice(0, 2))" :key="peer.uid"
                  class="d-flex flex-row participant-item">
                  <div class="flex-grow-1">
                    <div class="d-flex flex-row align-items-center">
                      <div class="participant-name">
                        {{ peer.uname }}
                      </div>
                      <b-badge v-if="peer.role === 'admin'" variant="light" class="ml-2 badge-operator">
                        {{ $t("meetingOperatorBadge") }}
                      </b-badge>
                    </div>
                    <b-row class="mt-1">
                      <b-col cols="12" class="participant-detail">
                        <div>
                          <b-icon class="mr-1" icon="clock" />
                          {{ $t("joinMeeting") }}: {{ peer.durationStr }}
                        </div>
                      </b-col>
                    </b-row>
                  </div>
                  <div class="d-flex flex-column align-items-end justify-content-center text-muted" style="font-size: 13px; gap: 2px;">
                    <div>{{ $t("joinMeeting") }}: {{ peer.logTime }}</div>
                    <div>{{ $t("leaveMeeting") }}: {{ peer.lastLogTime }}</div>
                  </div>
                </b-list-group-item>
              </div>
            </b-list-group>

            <div v-if="peers.length > 2 && hasMeetingContent()" class="text-right mt-2">
              <b-button style="border: none;" variant="outline-primary" size="sm" @click="toggleView">
                {{ showAll ? $t("mini") : $t("viewmore") }}
                <span v-if="showAll"><b-icon-chevron-up class="ml-1" /></span>
                <span v-if="!showAll"><b-icon-chevron-down class="ml-1" /></span>
              </b-button>
            </div>

            <div v-if="meeting.status == 'END' && hasMeetingContent()" class="mt-3">
              <div class="d-flex flex-column mb-2">
                <h5 class="section-title mb-1">{{ $t("contentMeeting") }}</h5>
                <p style="font-style: italic; color: #6c757d; font-size: 12px; margin-top: 0; margin-bottom: 0; line-height: 1.2; letter-spacing: -0.2px;">
                  {{ $t('aiSummaryDisclaimer') }}
                </p>
              </div>

              <!-- Segmented selector for tabs -->
              <div v-if="contentTabOptions.length > 0" class="mb-2">
                <b-form-radio-group class="custom-radio-group" size="sm" buttons button-variant="outline-primary"
                  v-model="activeContentTab"
                  :options="contentTabOptions" />
              </div>

              <!-- Summary content container inside custom card -->
              <div class="summary-card">
                <!-- Floating action buttons inside card (pinned in place) -->
                <div class="floating-content-actions">
                  <b-button class="action-btn" size="sm" @click="$bvModal.show('expanded-content-modal')" v-b-tooltip.hover title="Xem chi tiết">
                    <b-icon-eye />
                  </b-button>
                  <b-button class="action-btn" size="sm" @click="downloadActiveTabDoc" v-b-tooltip.hover title="Tải xuống (.doc)">
                    <b-icon-download />
                  </b-button>
                </div>

                <!-- Scrollable content area -->
                <div class="text-tab">
                  <!-- Tab tóm tắt chung -->
                  <div v-if="activeContentTab === 'general' && summaryArry.length > 0">
                    <div v-for="(item, index) in summaryArry" :key="index" class="mb-4">
                      <div class="summary-type-header">{{ getSummaryType(index) }}</div>
                      <div v-html="formattedSummaryMeeting(item)" class="tab-content-html"></div>
                    </div>
                  </div>

                  <!-- Tab Summary of Timeline -->
                  <div v-if="activeContentTab === 'timeline' && summaryByTimeline.length > 0">
                    <div v-for="(html, index) in formattedTimelineList" :key="'timeline-' + index" v-html="html" class="tab-content-html mb-3"></div>
                  </div>

                  <!-- Tab tóm tắt theo người phát biểu -->
                  <div v-if="activeContentTab === 'speaker' && summaryAllUser.length > 0">
                    <div v-for="(item, index) in summaryAllUser" :key="index" class="mb-3">
                      <b class="text-dark d-block mb-1">{{ item.name }}</b>
                      <div v-html="formatSummary(item.summary)" class="tab-content-html"></div>
                    </div>
                  </div>

                  <!-- Export tab -->
                  <div v-if="activeContentTab === 'content' && contentRoom">
                    <div v-html="formattedContentRoom" class="tab-content-html"></div>
                  </div>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </template>
    </b-container>

    <ExpandedContentModal
      modal-id="expanded-content-modal"
      :active-tab-title="activeTabTitle"
      :active-content-tab="activeContentTab"
      :summary-arry="summaryArry"
      :summary-by-timeline="summaryByTimeline"
      :summary-all-user="summaryAllUser"
      :content-room="contentRoom"
      :formatted-timeline-list="formattedTimelineList"
      :formatted-content-room="formattedContentRoom"
      @download="downloadActiveTabDoc"
    />

    <b-modal id="new-participant-modal" :title="$t('addMeetingParticipant')" hide-footer no-close-on-backdrop>
      <NewMeetingParticipantModal modal-id="new-participant-modal" :meeting-id="meetingId" @submitted="fetchMeeting" />
    </b-modal>

    <b-modal id="update-participant-modal" :title="$t('updateMeetingParticipant')" hide-footer no-close-on-backdrop>
      <UpdateMeetingParticipantModal modal-id="update-participant-modal" :meeting-id="meetingId"
        :meeting-participant="participantToUpdate" @submitted="fetchMeeting" />
    </b-modal>

    <b-modal id="send-meeting-email-confirm-modal" size="lg" :title="emailStatusActionModalQuestionText" hide-footer
      no-close-on-backdrop>
      <send-meeting-email-confirm-modal modal-id="send-meeting-email-confirm-modal" :meeting-id="meetingId"
        :status="emailStatus" @submitted="fetchMeeting" />
    </b-modal>
    <MeetingOtherInfoModal
      v-if="meeting"
      modal-id="qr-code-modal"
      :meeting="meeting"
      :participant-join-url="participantJoinUrl"
    />

     <b-modal id="edit-max-participant-modal" :title="$t('changeQuantity')" hide-footer no-close-on-backdrop>
      <EditMaxParticipantModal v-if="meeting" modal-id="edit-max-participant-modal" :meeting-id="meetingId" @submitted="fetchMeeting" :max-participants="meeting.numOfParticipants"/>
    </b-modal>
     <b-modal id="edit-max-cam-modal" :title="$t('changeQuantity')" hide-footer no-close-on-backdrop>
      <EditMaxCamModal v-if="meeting" modal-id="edit-max-cam-modal" :meeting-id="meetingId" @submitted="fetchMeeting" :max-cam-value="meeting.maxCam"/>
    </b-modal>
    <WarmingModal ref="startMeetingWarningModal"/>
  </main>
</template>


<style lang="scss" scoped>
.custom-hr {
  margin-top:20px!important;
  margin-bottom: 15px!important;
}
.mb-2-c
{
  margin-bottom: 20px!important;
}
.mt-2-c
{
  margin-top: 15px!important;
}
.recording-link
{
  width: 98%!important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.play-class {
  font-size: 28px!important;
}
.break-word-max
{
  max-width: 60vw;
  word-wrap: break-word;
}
#conf-info-2 {
  line-height: 160%;
}

.qr-code {
  width: 250px;
  height: 250px;
  border: 1px solid;
  padding: 1.5em;
  border-radius: 1em;
  display: flex;
}

.qr-button {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
}

.header-qr {
  border: none;
}
.header-record
{
  font-weight: 600!important;
}
.list-group-item {
  border-radius: 0.2rem !important;
  gap: 5px !important;
  border-top-width: 1px !important;
  margin-bottom: 5px !important;
  padding: 0.35rem 1.25rem !important;
}

.list-group-item:last-child {
  margin-bottom: 0px !important;
}
.recording-progress {
  color: #63C454;
}
.recording-progress .b-icon {
  fill: #63C454;
  font-size: 20px;
}
.text-tab{
  max-height: 40vh;
  overflow-y: scroll;
  padding:10px;
  font-size: 16px;
}

.recording-popup {
  position: absolute;
  top: calc(100% + 15px);
  left: 0;
  background: white;
  border-radius: 12px;
  padding: 20px;
  width: 480px;
  z-index: 1000;
  border: 1px solid #d1d5db;
  text-align: left;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;

  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: 40px;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid white;
  }

  // Invisible bridge to cover the 15px gap between button and popup
  &::after {
    content: '';
    position: absolute;
    top: -15px;
    left: 0;
    width: 100%;
    height: 15px;
  }
}

.close-popup-icon {
  position: absolute;
  top: 12px;
  right: 12px;
  cursor: pointer;
  font-size: 20px;
  color: #6b7280;
  transition: color 0.15s;
  z-index: 10;
}

.close-popup-icon:hover {
  color: #111827;
}

.popup-title {
  font-weight: 600;
  margin-bottom: 12px;
  font-size: 15px;
}

.recording-item {
  margin-bottom: 10px;
  border-radius: 8px;
  padding: 12px;
  background-color: #f8fafc !important;
  border: 1px solid #e5e5e5;

  &:last-child {
    margin-bottom: 0;
  }
}

.recording-link {
  color: #2f55a4;
  text-decoration: underline;
  font-size: 13.5px;
  margin-bottom: 10px;
  cursor: pointer;
  display: block;
  word-break: break-all;
}

.recording-info {
  display: flex;
  align-items: center;
  font-size: 12px;
  gap: 12px;
}

.badge-audio {
  background: #e0ebff;
  color: #1a56db;
  padding: 4px 10px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 11.5px;
}

.badge-video {
  background: #fde8e8;
  color: #9b1c1c;
  padding: 4px 10px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 11.5px;
}

.size-pill {
  border: 1px solid #e0e0e0;
  background: white;
  color: #333;
  padding: 4px 16px;
  border-radius: 20px;
  font-size: 13px;
}

.recording-actions {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-left: auto;
}

.action-icon {
  cursor: pointer;
  font-size: 18px;
  transition: color 0.15s ease-in-out;

  &.icon-copy {
    color: #6b7280;
  }
  &.icon-view {
    color: #16a34a;
  }
  &.icon-download {
    color: #2563eb;
  }
  &.icon-delete {
    color: #dc2626;
  }

  &:hover {
    &.icon-copy {
      color: #374151;
    }
    &.icon-view {
      color: #15803d;
    }
    &.icon-download {
      color: #1d4ed8;
    }
    &.icon-delete {
      color: #b91c1c;
    }
  }
}

// hr {
//   margin: 20px 0;
//   border: 0;
//   border-top: 1px solid #eee;
// }
.pill-size {
  border: 1px solid #e0e0e0;
  background: white;
  color: #333;
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.meta-card {
  background-color: #f2f5ff82;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  // border-bottom: 1px solid #e5e5e5;
  font-size: 14.5px;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
}

.meta-label {
  display: flex;
  align-items: center;
  font-weight: 500;
  gap: 8px;

  .b-icon {
    font-size: 15px;
  }
}

.meta-value {
  font-weight: 600;
  text-align: right;
}
.link-card-modern {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: #f1f5f9;
    border-color: #cbd5e1;
  }

  &.host-card {
    background-color: #f0f7ff;
    border-color: #bfdbfe;

    &:hover {
      background-color: #e6f0fa;
      border-color: #93c5fd;
    }

    .link-card-label {
      color: #1e40af;

      svg,
      .b-icon {
        color: #2563eb;
      }
    }
  }

  &.participant-card {
    background-color: #f8fafc;
    border-color: #e2e8f0;

    &:hover {
      background-color: #f1f5f9;
      border-color: #cbd5e1;
    }

    .link-card-label {
      color: #475569;

      svg,
      .b-icon {
        color: #2563eb;
      }
    }
  }
}

.link-card-info {
  flex: 1;
  min-width: 0;
}

.link-card-label {
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;

  .b-icon {
    font-size: 13px;
    color: #2563eb;
  }
}

.link-card-url {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;

  a {
    display: inline-block;
    max-width: 100%;
    color: #1d4ed8;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: #1e40af;
      text-decoration: underline;
    }
  }
}

.link-card-action {
  flex-shrink: 0;
}

.btn-copy-modern {
  border: 1px solid #cbd5e1 !important;
  background-color: #ffffff !important;
  color: #475569 !important;
  border-radius: 8px !important;
  width: 32px;
  height: 32px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  transition: all 0.2s ease !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;

  &:hover {
    background-color: #2f55a4 !important;
    border-color: #2f55a4 !important;
    color: #ffffff !important;
  }

  &:focus {
    box-shadow: 0 0 0 2px rgba(47, 85, 164, 0.2) !important;
  }
}

.code-card-modern {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  min-width: 140px;
}

.code-card-label {
  font-size: 11.5px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.code-card-value-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.code-card-value {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.btn-copy-mini {
  border: none !important;
  background-color: transparent !important;
  color: #64748b !important;
  padding: 0 !important;
  width: 24px;
  height: 24px;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  border-radius: 6px !important;
  transition: all 0.15s ease !important;

  &:hover {
    background-color: #f1f5f9 !important;
    color: #0f172a !important;
  }
}

.custom-alert {
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-size: 14.5px !important;
  font-weight: 500 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  border-width: 1px !important;
  border-style: solid !important;
  margin-top: 16px !important;
  margin-bottom: 24px !important;
  box-shadow: none !important;

  &.alert-warning {
    background-color: #f5f5f5 !important;
    border-color: #ffe0b3 !important;
    color: #8c5800 !important;

    .alert-icon {
      color: #b37100 !important;
      font-size: 18px;
    }

    .btn-alert-action {
      color: #8c5800 !important;
      border-color: #ffe0b3 !important;
      background-color: transparent !important;

      &:hover {
        background-color: #ffe0b3 !important;
      }
    }
  }

  &.alert-info {
    background-color: #f2f5ff !important;
    border-color: #d2deff !important;
    color: #1a365d !important;

    .alert-icon {
      color: #2f55a4 !important;
      font-size: 18px;
    }

    .btn-alert-action {
      color: #2f55a4 !important;
      border-color: #d2deff !important;
      background-color: transparent !important;

      &:hover {
        background-color: #d2deff !important;
      }
    }
  }

  .btn-alert-action {
    font-weight: 600 !important;
    border-radius: 8px !important;
    padding: 6px 16px !important;
    font-size: 13.5px !important;
    transition: all 0.2s ease-in-out !important;
  }
}

.btn-circle-monitor {
  border-radius: 50% !important;
  width: 24px;
  height: 24px;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  font-size: 11px !important;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spin-icon {
  animation: spin 2s linear infinite;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 0;
}

.participant-item {
  background-color: #f2f5ff82 !important;
  border: 1px solid #e5e5e5 !important;
  border-radius: 12px !important;
  padding: 12px 16px !important;
  margin-bottom: 8px !important;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: #eef2ff !important;
    border-color: #d2deff !important;
  }

  &:last-child {
    margin-bottom: 0 !important;
  }
}

.participant-name {
  font-size: 15px;
  display: flex;
  align-items: center;
}

.participant-detail {
  font-size: 13px;
  color: #4b5563;
  display: flex;
  align-items: center;

  .b-icon {
    color: #6b7280;
    font-size: 14px;
  }
}

.badge-operator {
  background-color: #e0ebff !important;
  color: #1a56db !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  padding: 3px 8px !important;
  border-radius: 6px !important;
  border: 1px solid #c3ddfd !important;
}

.custom-radio-group {
  background-color: #f3f4f6 !important;
  padding: 4px !important;
  border-radius: 10px !important;
  border: none !important;
  display: inline-flex !important;

  ::v-deep .btn {
    border: none !important;
    border-radius: 8px !important;
    font-size: 12.5px !important;
    font-weight: 600 !important;
    padding: 6px 14px !important;
    margin: 0 !important;
    color: #4b5563 !important;
    background-color: transparent !important;
    box-shadow: none !important;
    transition: all 0.2s ease !important;

    &.active {
      color: #2f55a4 !important;
      background-color: #ffffff !important;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
    }
  }
}

.summary-card {
  background-color: #f2f5ff82;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);
  position: relative;
}

.floating-content-actions {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  z-index: 100;
}

.floating-content-actions .action-btn {
  background: rgba(255, 255, 255, 0.75) !important;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(229, 231, 235, 0.9) !important;
  color: #4b5563 !important;
  padding: 6px 8px !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.2s ease-in-out;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.floating-content-actions .action-btn:hover {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #1f2937 !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08) !important;
}

::v-deep .custom-summary-tabs {
  border-bottom: 1px solid #e5e5e5 !important;
  background-color: #f8fafc !important;
  padding: 8px 16px 0 16px !important;
  gap: 4px;

  .nav-link {
    border: none !important;
    color: #4b5563 !important;
    font-weight: 600 !important;
    font-size: 13.5px !important;
    padding: 10px 16px !important;
    margin-bottom: 0 !important;
    border-bottom: 2px solid transparent !important;
    transition: all 0.15s ease-in-out !important;
    background: transparent !important;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;

    &:hover {
      color: #111827 !important;
    }

    &.active {
      color: #2f55a4 !important;
      border-bottom-color: #2f55a4 !important;
      background: transparent !important;
    }
  }
}

.text-tab {
  max-height: 70vh;
  overflow-y: auto !important;
  padding: 20px !important;
  font-size: 14.5px !important;
  color: #374151 !important;
  background-color: #f2f5ff82 !important;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #d1d5db;
    border-radius: 3px;
  }
}

.summary-type-header {
  font-size: 18px !important;
  line-height: 1.7;
  color: #004f9e !important;
  font-weight: 700 !important;
  margin-top: 12px;
  margin-bottom: 8px;
}

::v-deep .tab-content-html {
  line-height: 1.7;
  font-size: 14.5px;
  color: #374151;

  // Tiêu đề
  h1, h2, h3, h4, h5, h6 {
    font-family: inherit;
    color: #475569 !important;
    font-weight: 600 !important;
    margin-top: 1.25rem !important;
    margin-bottom: 0.5rem !important;
    line-height: 1.3 !important;
  }

  h1 { font-size: 16.5px !important; }
  h2 { font-size: 15.5px !important; margin-top: 1rem !important; }
  h3 { font-size: 14.5px !important; }
  h4 { font-size: 14px !important; }

  // Các đoạn văn
  p {
    margin-bottom: 0.5rem !important;
  }

  div {
    margin-bottom: 0.25rem !important;
  }

  // Danh sách
  ul, ol {
    margin-top: 0.2rem !important;
    margin-bottom: 0.5rem !important;
    padding-left: 1.25rem !important;

    li {
      margin-bottom: 0.2rem !important;
      line-height: 1.5 !important;
    }
  }

  ul {
    list-style-type: disc !important;
  }

  ol {
    list-style-type: decimal !important;
  }

  // In đậm & in nghiêng
  strong {
    color: #0f172a; // Slate-900 giúp chữ in đậm sắc nét
    font-weight: 600;
  }

  em {
    color: #4b5563;
  }

  // Bảng biểu
  table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    margin-top: 0.75rem;
    margin-bottom: 1.5rem;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    th, td {
      padding: 10px 14px;
      font-size: 13.5px;
      line-height: 1.5;
    }

    th {
      background-color: #f8fafc;
      color: #1e293b;
      font-weight: 600;
      border-bottom: 1px solid #cbd5e1;
      border-right: 1px solid #e2e8f0;
      text-align: left;

      &:last-child {
        border-right: none;
      }
    }

    td {
      color: #334155;
      border-bottom: 1px solid #f1f5f9;
      border-right: 1px solid #f1f5f9;
      background-color: #ffffff;

      &:last-child {
        border-right: none;
      }
    }

    tr:last-child td {
      border-bottom: none;
    }

    tr:nth-child(even) td {
      background-color: #f8fafc; // Xen kẽ màu dòng
    }
  }

  // Liên kết & mã code inline
  a {
    color: #2563eb;
    text-decoration: underline;
    transition: color 0.15s ease;

    &:hover {
      color: #1d4ed8;
    }
  }

  code {
    font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
    background-color: #f1f5f9;
    color: #e11d48;
    padding: 0.2em 0.4em;
    border-radius: 4px;
    font-size: 85%;
    font-weight: normal;
  }

  // Khối mã nguồn
  pre {
    background-color: #0f172a;
    border-radius: 6px;
    padding: 12px 16px;
    margin: 1rem 0;
    overflow-x: auto;
    border: 1px solid #1e293b;

    code {
      background-color: transparent;
      color: #f8fafc;
      padding: 0;
      font-size: 13px;
      font-family: inherit;
    }
  }

  // Khối trích dẫn (Blockquote)
  div[style*="border-left"] {
    border-left: 4px solid #3b82f6 !important;
    background-color: #eff6ff;
    padding: 12px 16px !important;
    border-radius: 0 6px 6px 0;
    margin: 1.25rem 0 !important;
    color: #1e3a8a !important;
    font-size: 14px;
    font-style: normal !important;

    div {
      margin-bottom: 0;
      color: inherit;
    }
  }

  hr {
    border: none;
    border-top: 1px solid #e2e8f0;
    margin: 1.5rem 0;
  }
}
</style>


<script lang="ts">
import { Component, Vue, Watch, Ref } from 'vue-property-decorator';
import _ from "lodash";
import BaseComponent from "@/components/BaseComponent";
import * as meetingService from "@/service/meetingService";
import * as util from "@/service/util";
import { confirmStartMeetingWithoutRecording, confirmStartMeetingWithoutAI, confirmStartMeetingWithoutBoth } from "@/service/meetingAlertUtils";
import * as customerService from "@/service/customerService";
import ReadOnlyCheckBox from "@/components/ReadOnlyCheckBox.vue";
import NewMeetingParticipantModal from "@/components/meeting/NewMeetingParticipantModal.vue";
import EditMaxParticipantModal from '@/components/meeting/EditMaxParticipantModal.vue';
import EditMaxCamModal from '@/components/meeting/EditMaxCamModal.vue';
import UpdateMeetingParticipantModal from "@/components/meeting/UpdateMeetingParticipantModal.vue";
import SendMeetingEmailConfirmModal from "@/components/meeting/SendMeetingEmailConfirmModal.vue";
import ExpandedContentModal from "@/components/meeting/ExpandedContentModal.vue";
import { getCoreDomain } from '@/service/util';
import * as userService from "@/service/userService";
import DomainSelectModal from "@/views/domain/DomainSelectModal.vue";
import { Status } from '../enums/EnableStatus';
import { MeetingStatus } from '../enums/MeetingStatus';
import * as exportService from "@/service/exportService";
import { featureCode } from '../enums/featureCode';
import * as meetingMarkdownMock from "@/mock/meetingMarkdownMock";
import ManageStorageModal from "@/components/storage/ManageStorageModal.vue";
import WarmingModal from "@/components/warning/WarmingModal.vue";
import MeetingOtherInfoModal from "@/components/meeting/MeetingOtherInfoModal.vue";
import Badge from "@/components/helpers/Badge.vue";

@Component({
  components: {
    NewMeetingParticipantModal,
    UpdateMeetingParticipantModal,
    ReadOnlyCheckBox,
    SendMeetingEmailConfirmModal,
    DomainSelectModal,
    EditMaxParticipantModal,
    EditMaxCamModal,
    ExpandedContentModal,
    ManageStorageModal,
    WarmingModal,
    MeetingOtherInfoModal,
    Badge
  }
})
export default class MeetingDetails extends BaseComponent {

  readonly statusText = {
    PENDING: this.$t("meetingStatusPending").toString(),
    INPROGRESS: this.$t("meetingStatusInProgress").toString(),
    END: this.$t("meetingStatusEnded").toString(),
    CANCEL: this.$t("meetingStatusCancelled").toString(),
  };
  enumMeetingStatus =MeetingStatus;
  readonly statusBadgeVariant = {
    PENDING: "warning",
    INPROGRESS: "success",
    END: "secondary",
    CANCEL: "danger"
  };

  meetingId: number = null;
  isLoading = false;
  meeting = null;
  participantJoinUrl = '';
  emailStatus = "";
  emailStatusAlertVariant = "info";
  emailStatusAlertText = "";
  emailStatusButtonText = "";
  emailStatusActionModalQuestionText = "";
  emailStatusActionSuccessText = "";
  summaryArry = [];
  user = userService.getCurrentUser() || null;
  isWebinar: boolean = false;

  inProgressMeetingParticipantsViewMode: "joined" | "notJoined" = "joined";
  inProgressMeetingParticipantsViewModeOptions = [
    { value: "joined", text: this.$t("meetingParticipantsJoined") },
    { value: "notJoined", text: this.$t("meetingParticipantsNotJoined") },
  ];
  temp: any;

  activeContentTab: string = 'general';
  isContentExpanded = false;

  get contentTabOptions() {
    const options = [];
    if (this.summaryArry && this.summaryArry.length > 0) {
      options.push({ value: 'general', text: this.$t('generalContent').toString() });
    }
    if (this.summaryByTimeline && this.summaryByTimeline.length > 0) {
      options.push({ value: 'timeline', text: this.$t('summaryOfTimeline').toString() });
    }
    if (this.summaryAllUser && this.summaryAllUser.length > 0) {
      options.push({ value: 'speaker', text: this.$t('summaryOfAllUser').toString() });
    }
    if (this.contentRoom) {
      options.push({ value: 'content', text: this.$t('transcript').toString() });
    }
    return options;
  }

  get activeTabTitle() {
    const activeOpt = this.contentTabOptions.find((opt: any) => opt.value === this.activeContentTab);
    return activeOpt ? activeOpt.text : this.$t("contentMeeting").toString();
  }

  @Watch('contentTabOptions')
  onContentTabOptionsChange(newOptions: any) {
    if (newOptions && newOptions.length > 0) {
      const exists = newOptions.some((opt: any) => opt.value === this.activeContentTab);
      if (!exists) {
        this.activeContentTab = newOptions[0].value;
      }
    }
  }

  get showParticipantsJoined() {
    return this.inProgressMeetingParticipantsViewMode === "joined" ? this.meeting.participantsJoined : this.meeting.participantsNotJoined;
  }

  get appGmUrl() {
    return window.location.origin + '/app/#/gm';
  }

  get hostJoinUrlWithAdminCode(): string {
    if (!this.meeting || !this.meeting.adminCode) {
      return "";
    }
    const adminCode = this.meeting.adminCode.toString().trim();
    if (!adminCode) {
      return "";
    }
    let hostUrl = this.meeting.hostJoinUrl;
    if (!hostUrl) {
      const secretKey = this.meeting.meetingSecretKey || this.meeting.secretKey;
      if (secretKey) {
        hostUrl = window.location.origin + '/app/#/host/' + secretKey;
      }
    }
    if (!hostUrl) return "";
    if (!hostUrl.startsWith('http')) {
      hostUrl = this.formatUrl(hostUrl);
    }
    // Đảm bảo param accessCode luôn lấy chính là adminCode
    if (/([?&])accessCode=[^&]*/.test(hostUrl)) {
      hostUrl = hostUrl.replace(/([?&])accessCode=[^&]*/, `$1accessCode=${encodeURIComponent(adminCode)}`);
    } else if (/([?&])adminCode=[^&]*/.test(hostUrl)) {
      hostUrl = hostUrl.replace(/([?&])adminCode=[^&]*/, `$1accessCode=${encodeURIComponent(adminCode)}`);
    } else {
      const separator = hostUrl.includes("?") ? "&" : "?";
      hostUrl = `${hostUrl}${separator}accessCode=${encodeURIComponent(adminCode)}`;
    }
    return hostUrl;
  }

  participantToUpdate = null;
  isLoadingRecordUrl = false;
  playbackUrl: string = null;
  peers = [];
  peersExportfilelog = [];
  summaryByTimeline = [];
  contentRoom: string = null;
  summaryAllUser = [];
  showAll = true;
  meetingFeatures=[];
  selectedWhiteboards: Array<{ id: string | number; name: string; accessLink: string }> = [];
  // sumaryMeeting: string = null;
  showDomainSelectModal = false;
  showRecordingPopup = false;
  recordingLinks: any = null;


  mounted() {
    this.meetingId = +this.$route.params.id || null;
    this.fetchMeeting();
    this.coreDomainTxt;
    this.checkAuthFaceIdFeature();
    //
    document.addEventListener('click', this.handleGlobalClick);
  }

  beforeDestroy() {
  document.removeEventListener('click', this.handleGlobalClick);
}

handleGlobalClick(event: MouseEvent) {
  const popup = this.$el.querySelector('.recording-popup');
  const button = this.$el.querySelector('.btn-recording-toggle'); // Thêm class này vào nút bấm

  if (this.showRecordingPopup && popup && !popup.contains(event.target as Node) && !button?.contains(event.target as Node)) {
    this.showRecordingPopup = false;
  }
}

  toggleView() {
    this.showAll = !this.showAll;
  }

  openRecordingPopup() {
    this.showRecordingPopup = true;
  }

  closeRecordingPopup() {
    this.showRecordingPopup = false;
  }


  formatDate(date) {
    return util.localDateTimeFormat(date);
  }

  async copiedLinkQR() {
    await navigator.clipboard.writeText(this.participantJoinUrl);
    this.toastSuccessDelay(this.$t("copied").toString());
  }



  async fetchMeeting() {
    try {
      this.isLoading = true;
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      try {
        this.meeting = await meetingService.getDetails(this.meetingId);
      } catch (error) {
        console.log('error', error);
        throw error;
      }

      ///Lấy toàn bộ tính năng cuộc họp (không lọc theo trạng thái kích hoạt)
      this.meetingFeatures = this.meeting.meetingFeatures;
      this.isWebinar = this.meetingFeatures?.some(
          (f: any) => f.featureCode === featureCode.ALLOW_WEBINAR && f.isActive === Status.ENABLE
      );

      // Load whiteboard info
      this.selectedWhiteboards = (this.meeting.whiteboards || []).map(wb => ({
        id: wb.boardUid,
        name: wb.name,
        accessLink: wb.accessLink
      }));
      if (this.meeting.status === MeetingStatus.END && (this.isOwnerOrHost || this.isUserAdmin())) {
        await this.loadRecordingUrl();
        this.GetParticipantsMeeting();

        /// Lấy dữ liệu nội dung cuộc họp
        if (!isLocalhost) {
          /// Gọi API để lấy dữ liệu về nội dung cuộc họp
          if (this.meeting.activeVoiceText ?? false) {
            this.getSummaryAllUser();
            this.getContentMeeting();
            this.getSummaryMeeting();
            this.fetchMeetingSummaryByTimeline();
          }
        } else {
          /// MOCK dữ liệu về nội dung cuộc họp
          this.meeting.status = MeetingStatus.END;
          this.meeting.activeVoiceText = true;
          this.summaryArry = meetingMarkdownMock.mockSummaryArry;
          this.summaryByTimeline = meetingMarkdownMock.mockSummaryByTimeline;
          this.summaryAllUser = meetingMarkdownMock.mockSummaryAllUser;
          this.contentRoom = meetingMarkdownMock.mockContentRoom;
          this.showAll = false;
          /// END MOCK
        }
      }
      this.formatJoinUrls();
    } catch (error) {
      console.error("Error fetching meeting:", error);
      this.toastError(error);
    } finally {
      this.isLoading = false;
    }
    this.processParticipantViewOptions();
  }

  openWhiteboardLink(accessLink: string) {
    if (accessLink) {
      window.open(accessLink, '_blank');
    }
  }

  async getSummaryAllUser() {
    try {
      this.summaryAllUser = await meetingService.getSummaryAllUser({ mid: this.meeting.internalMeetingId });
    } catch (e) {
      console.error(e??"Error fetching contentRoom");
    }
  }
  async getContentMeeting() {
    try {
      this.contentRoom = await meetingService.getContentMeeting({ mid: this.meeting.internalMeetingId });
    } catch (e) {
      console.error(e??"Error fetching contentRoom");
    }
  }
  async getSummaryMeeting() {
    try {
      this.summaryArry = await meetingService.getSummaryMeeting({ mid: this.meeting.internalMeetingId });
      this.showAll = this.summaryArry.length === 0;
    } catch (e) {
      console.error(e??"Error fetching sumaryMeeting");
    }
  }

  ///thực hiện các API để lấy LOG người tham gia cuộc họp và xuất file log
  async GetParticipantsMeeting() {
    try {
      this.peers = await meetingService.getParticipantsMeeting({ mid: this.meeting.internalMeetingId });
    }
    catch (e) {
      console.error(e);
    }
  }

  processParticipantViewOptions() {
    try {
      if (this.meeting.participantsJoined) {
        const notJoinedCount = util.countPredicate(
          this.meeting.meetingParticipants,
         m => this.meeting.participantsJoined.every(pj => pj.name !== m.name)
        );
        this.inProgressMeetingParticipantsViewModeOptions = [
          {
            value: "joined",
            text: this.$t("meetingParticipantsJoined", {
              count: this.meeting.participantsJoined.length
            }),
          },
          {
            value: "notJoined",
            text: this.$t("meetingParticipantsNotJoined", {
              count: notJoinedCount
            }),
          },
        ];
      }
    } catch (error) {
      console.error(error);
    }
  }


  //lấy file record cuộc họp
  async loadRecordingUrl() {
    this.isLoadingRecordUrl = true;
    try {
      const res = await meetingService.getMeetingRecording(this.meetingId);
      this.playbackUrl = res.playbackUrl;
      // Lấy danh sách link ghi âm (Audio & Video)
      this.recordingLinks = await meetingService.getMeetingRecordingLinks(this.meetingId);
    } catch (e) {
      console.error("Error loading recording links:", e);
    } finally {
      this.isLoadingRecordUrl = false;
    }
  }

  openRecording(url: string) {
    if (!url) return;
    window.open(url, "_blank");
  }

  async copyText(text: string) {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    this.toastSuccessDelay(this.$t("copied").toString());
  }

  /**
   * Tải xuống file ghi âm cuộc họp (hỗ trợ file dung lượng lớn tới 10GB+ không gây crash trình duyệt)
   * @param url Link tải file ghi âm
   * @param filename Tên file mong muốn khi tải về
   */
  async downloadRecording(url: string, filename: string) {
    await util.downloadFile(url, filename, this.openRecording);
  }

  //Link cuộc họp
  formatJoinUrls() {
    if (this.meeting.participantJoinUrl) {
      this.meeting.participantJoinUrl = this.formatUrl(this.meeting.participantJoinUrl);
      this.participantJoinUrl = this.meeting.participantJoinUrl;
    }
    if (this.meeting.hostJoinUrl) {
      this.meeting.hostJoinUrl = this.formatUrl(this.meeting.hostJoinUrl);
    }
  }

  @Watch("meeting", { immediate: true, deep: true })
  onMeetingChange(meeting: any) {
    // Reset
    this.emailStatus = "";
    this.emailStatusAlertVariant = "info";
    this.emailStatusAlertText = "";
    this.emailStatusButtonText = "";
    this.emailStatusActionModalQuestionText = "";
    this.emailStatusActionSuccessText = "";
    if (!meeting) return;
 if ([MeetingStatus.PENDING, MeetingStatus.INPROGRESS].includes(meeting.status)) {
      if (meeting.invitationSent >= 0) {
        if (meeting.invitationSent === 0) {
          this.emailStatus = "NEW";
          this.emailStatusAlertVariant = "warning";
          this.emailStatusAlertText = this.$t("notSendEmailInvitation").toString();
          this.emailStatusButtonText = this.$t("sendEmailInvitation2").toString();
          this.emailStatusActionModalQuestionText = (meeting.publicMeeting ?
            this.$t("sendEmailInvitation2").toString() :
            this.$t("sendEmailInvitationToAll").toString()
          ) + "?";
          this.emailStatusActionSuccessText = this.$t("sendEmailInvitationSuccessful").toString();
        } else {
          this.emailStatusAlertText = this.$t("alreadySentEmailInvitation").toString();
        }
      }
      if (meeting.updateSent >= 0) {
        if (meeting.updateSent === 0) {
          this.emailStatus = "UPDATE";
          this.emailStatusAlertVariant = "warning";
          this.emailStatusAlertText = this.$t("notSendMeetingUpdateEmail").toString();
          this.emailStatusButtonText = this.$t("sendMeetingUpdateEmail").toString();
          this.emailStatusActionModalQuestionText = (meeting.publicMeeting ?
            this.$t("sendMeetingUpdateEmail").toString() :
            this.$t("sendMeetingUpdateEmailToAll").toString()
          ) + "?";
          this.emailStatusActionSuccessText = this.$t("sendMeetingUpdateEmailSuccessful").toString();
        } else {
          this.emailStatusAlertText = this.$t("alreadySentMeetingUpdateEmail").toString();
        }
      }
    }

    if (meeting.cancellationSent >= 0 && meeting.status === MeetingStatus.CANCEL) {
      if (meeting.cancellationSent === 0) {
        this.emailStatus = "CANCEL";
        this.emailStatusAlertVariant = "warning";
        this.emailStatusAlertText = this.$t("notSendMeetingCancelEmail").toString();
        this.emailStatusButtonText = this.$t("sendMeetingCancelEmail").toString();
        this.emailStatusActionModalQuestionText = (meeting.publicMeeting ?
          this.$t("sendMeetingCancelEmail").toString() :
          this.$t("sendMeetingCancelEmailToAll").toString()
        ) + "?";
        this.emailStatusActionSuccessText = this.$t("sendMeetingCancelEmailSuccessful").toString();
      } else {
        this.emailStatusAlertText = this.$t("alreadySentMeetingCancelEmail").toString();
      }
    }
  }

  /**
   * Định dạng hiển thị tóm tắt chung lên giao diện.
   */
  formattedSummaryMeeting(stringSum) {
    const renumbered = util.renumberMarkdownHeadings(stringSum, 1);
    const formated = util.convertTitleSummaryMeeting(renumbered, true);
    return util.markdownToHtml(formated);
  }

  get isBBB() {
    return this.meeting?.corePlatform?.toLowerCase() === "bbb";
  }

  get isZoom() {
    return this.meeting?.corePlatform?.toLowerCase() === "zoom";
  }

  get actualTimeStr(): string {
    let ret = "-";
    if (this.meeting?.actualStartTime) {
      ret = this.localTimeFormat(this.meeting.actualStartTime);
      if (this.meeting.actualEndTime) {
        if(this.localDateFormat(this.meeting.actualStartTime) === this.localDateFormat(this.meeting.actualEndTime)){
          ret = this.localTimeFormat(this.meeting.actualStartTime) + " - " + this.localTimeFormat(this.meeting.actualEndTime);
        }
        else{
          ret = `${this.$t('from')} ${this.localTimeFormat(this.meeting.actualStartTime)} ${this.$t('day')} ${this.localDateFormat(this.meeting.actualStartTime)}
          ${this.$t('to')} ${this.localTimeFormat(this.meeting.actualEndTime)} ${this.$t('day')} ${this.localDateFormat(this.meeting.actualEndTime)}`;
        }
      }
    }
    return ret;
  }

  formatDurationFromRange() {
    let ret = " ";
      if (this.meeting.actualEndTime){
        const start = new Date(this.meeting.actualStartTime);
        const end = new Date(this.meeting.actualEndTime);
        // Tính chênh lệch theo milliseconds
        const diffMs = end.getTime() - start.getTime();
        //Tổng thời lượng (tính theo giây)
        const totalSeconds = Math.floor(diffMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        // Hàm pad để hiển thị định dạng 2 chữ số (vd: 03:09:07)
        const pad = (num: number) => num.toString().padStart(2, '0');

        ret = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
      }
    return ret;
  }

  get participantJoinUrlHasAccessCode() {
    return this.meeting?.participantJoinUrl?.includes("?accessCode=");
  }

  get operators(): any[] {
    return this.meeting?.meetingParticipants?.filter(it => it.role === "OPERATOR") || [];
  }

  get participants(): any[] {
    return this.meeting?.meetingParticipants?.filter(it => it.role !== "OPERATOR") || [];
  }

  get shouldShowSendAllSmsInvitationBtn(): boolean {
    return this.meeting && this.meeting.status !== MeetingStatus.END && this.meeting.meetingParticipants.some(p => p.phone);
  }

  get canAddParticipants(): boolean {
    return ![MeetingStatus.END, MeetingStatus.CANCEL].includes(this.meeting.status) && !this.meeting.publicMeeting && this.isOwnerOrHost;
  }

  get canEditMaxparticipant():boolean
  {
    return  ![MeetingStatus.END, MeetingStatus.CANCEL].includes(this.meeting.status);

  }
  get canEditMaxCam(): boolean {
    return this.meeting && [MeetingStatus.PENDING, MeetingStatus.INPROGRESS].includes(this.meeting.status);
  }

  get getMaxCam(): number {
    if (this.meeting) {
      if (this.meeting.maxCam != null) {
        return this.meeting.maxCam;
      }
      if (this.isUserAdmin()) {
        return this.meeting.numOfParticipants ?? 0;
      }
    }
    return 0;
  }
  get canChangeParticipants(): boolean {
    return this.isOwnerOrHost && ![MeetingStatus.END, MeetingStatus.CANCEL].includes(this.meeting.status);
  }

  get canDeleteRecording(): boolean {
    return !!(
      this.meeting &&
      this.meeting.isRecorded === 1 &&
      (this.meeting.recordingSize > 0 || this.meeting.recordingMp3Size > 0)
    );
  }

  /**
   * Kiểm tra người dùng hiện tại có phải là chủ sở hữu hoặc người chủ trì (host) của cuộc họp hay không.
   * @returns {boolean}
   */
  get isOwnerOrHost(): boolean {
    return !!(this.meeting && (this.meeting.owner || this.meeting.isHost));
  }

  get coreDomainTxt(): string {
    return this.meeting?.coreDomain ? getCoreDomain(this.meeting.coreDomain) : ""; // Handle null or undefined meeting
  }

  async onClickStartMeeting() {
    try {
      /// Lấy thông tin Khách hàng và cấu hình Feature
      this.showLoading();
      const [customer, featureConfig] = await Promise.all([
        customerService.getDetails(this.getUserCustomerId()),
        customerService.getFeatureConfigCustomer(this.getUserCustomerId())
      ]);
      this.hideLoading();

      const customerFeatures = featureConfig?.data?.features ?? [];

      // Xác định label nút dialog theo feature cấp khách hàng (giống Header.vue).
      // Chỉ dùng isActive, không dùng isDefault để tránh false positive từ feature mặc định của meeting.
      const hasSummaryFeature = customerFeatures.some(
        f => f.featureCode === featureCode.ALLOW_SUMMARY &&
             (f.isActive === Status.ENABLE || f.isActive === true)
      );
      const hasLiveCaptionFeature = customerFeatures.some(
        f => f.featureCode === featureCode.ALLOW_LIVECAPTION &&
             (f.isActive === Status.ENABLE || f.isActive === true)
      );
      const hasAIFeature = (hasSummaryFeature || hasLiveCaptionFeature) && this.checkAIFeature();
      const isAiQuotaLow = customer?.aiQuota?.isAiQuotaLowForMeeting ?? false;
      const isStorageLow = (customer?.isStorageLowForRecording ?? false) && this.checkRecordingFeature();

      // Cả 2 đều không đủ → hiển thị dialog gom chung
      if (hasAIFeature && isAiQuotaLow && isStorageLow) {
        await confirmStartMeetingWithoutBoth(
          this,
          customer,
          async () => { await this.handleStartMeeting(); },
          () => this.onClickOpenManageStorageModal()
        );
        return;
      }

      // 1. Kiểm tra hạn mức AI trước
      if (hasAIFeature && isAiQuotaLow) {
        await confirmStartMeetingWithoutAI(
          this,
          customer?.aiQuota?.cycleEnd,
          hasSummaryFeature,
          hasLiveCaptionFeature,
          async () => {
            // Đồng ý bắt đầu không sử dụng AI -> Kiểm tra tiếp dung lượng ghi hình
            await this.checkStorageBeforeStart(customer);
          }
        );
        return;
      }

      await this.checkStorageBeforeStart(customer);
    } catch (e) {
      this.handleStartMeetingError(e);
    }
  }

  async checkStorageBeforeStart(customer: any) {
    /// Kiểm tra dung lượng lưu trữ khi khởi tạo cuộc họp có ghi hình
    const isStorageLowForRecording = (customer.isStorageLowForRecording ?? false) && this.checkRecordingFeature();

    /// Nếu dung lượng lưu trữ thấp thì luôn hiển thị cảnh báo trước khi bắt đầu cuộc họp
    if (isStorageLowForRecording) {

      /// Hiển thị popup cảnh báo khi khởi tạo cuộc họp không đủ dung lượng
      await confirmStartMeetingWithoutRecording(
        this,
        customer,
        () => this.handleStartMeeting(),
        () => this.onClickOpenManageStorageModal()
      );
      return;
    }

    /// Hiển thị popup cảnh báo khi khởi tạo cuộc họp
    const confirm = await this.$bvModal.msgBoxConfirm(this.$t("startMeetingWarning").toString(), {
      okVariant: 'primary',
      okTitle: this.$t("ok").toString(),
      cancelTitle: this.$t("cancel").toString(),
    });
    if (!confirm) return;

    /// Khởi tạo cuộc họp
    await this.handleStartMeeting();
  }

  /**
   * Xử lý khởi tạo cuộc họp
   */
  private async handleStartMeeting() {
    ///
    try {
      this.showLoading();
      const res = await meetingService.startMeeting(this.meetingId);
      this.fetchMeeting();
      open(res.startUrl);
    } catch (e) {
      this.handleStartMeetingError(e);
    } finally {
      this.hideLoading();
    }
  }

  /**
   * Xử lý khi click vào nút tham gia cuộc họp
   */
  async onClickJoinMeeting() {
    try {
      this.showLoading();
      const res = await meetingService.joinMeeting(this.meetingId);
      this.fetchMeeting();
      open(res.joinUrl);
    } catch (e) {
      this.toastError(e);

    } finally {
      this.hideLoading();
    }
  }

  async onClickEndMeeting() {
    const modalMessage = this.$t("endThisMeetingWarning").toString();
    const confirm = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'danger',
      okTitle: this.$t("yes").toString(),
      cancelTitle: this.$t("no").toString(),
    });

    if (confirm) {
      try {
        this.showLoading();
        await meetingService.endMeeting(this.meetingId);
        this.fetchMeeting();
      } catch (e) {
        this.toastError(e);
      } finally {
        this.hideLoading();
      }
    }
  }

  async onClickCopyHostJoinUrl() {
    await navigator.clipboard.writeText(this.meeting.hostJoinUrl);
    this.toastSuccess(this.$t("alert").toString(), this.$t("copiedHostJoinUrl").toString());
  }


  async onClickCopyJoinUrl() {
    await navigator.clipboard.writeText(this.meeting.participantJoinUrl);
    this.toastSuccess(this.$t("alert").toString(), this.$t("copied").toString());
  }

/**
 * Link tham gia cuộc họp và các thành phần khi nhấn copy
 */
  async onClickCopyJoinUrlAndAccessCode() {
    let text = "";
    if (this.meeting.name) {
      text += `${this.meeting.name}\n`;
    }
    if (this.meeting.participantJoinUrl) {
      text += `${this.$t("accessLink")}: ${this.meeting.participantJoinUrl}\n`;
    }
    if (this.meeting.accessCode) {
      text += `${this.$t("accessCode")}: ${this.meeting.accessCode}`;
    }
    text = text.trim();

    // Ghi vào clipboard
    await navigator.clipboard.writeText(text);

    // Thông báo thành công
    this.toastSuccess(this.$t("alert").toString(), this.$t("copied").toString());
  }
  /**
   * Hàm copy thông tin (truyền giá trị muốn copy)
   */
  // async copyCode(value) {
  //   try {
  //     await navigator.clipboard.writeText(value);
  //     this.toastSuccess(this.$t("alert").toString(), this.$t("copied").toString());
  //   } catch (e) {
  //     this.toastError({ message: this.$t("copyFailed") });
  //   }
  // }

  /**
   * Hàm mở modal quản lý lưu trữ
   */
  private onClickOpenManageStorageModal() {
    this.$bvModal.show("manage-storage-modal");
  }


  async onClickEmailStatusAlertButton() {
    this.$bvModal.show("send-meeting-email-confirm-modal");
  }

  async onClickOpenQRCodeModal() {
    this.$bvModal.show("qr-code-modal");
  }


  async onClickSendAllEmailInvitation() {
    const modalMessage = this.$t("sendEmailInvitationToAll").toString() + "?";
    const confirm = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'primary',
      okTitle: this.$t("yes").toString(),
      cancelTitle: this.$t("no").toString(),
    });

    if (confirm) {
      try {
        this.showLoading();
        await meetingService.sendInvitationEmails(this.meetingId);
        this.toastSuccessDelay(this.$t("sendEmailInvitationSuccessful").toString());
      } catch (e) {
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }
  }

  async onClickSendSingleEmailInvitation(meetingParticipant) {
    const modalMessage = this.$t("sendEmailInvitationConfirm2", { email: meetingParticipant.email }).toString();
    const confirm = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'primary',
      okTitle: this.$t("yes").toString(),
      cancelTitle: this.$t("no").toString(),
    });
    if (confirm) {
      try {
        this.showLoading();
        await meetingService.sendSingleInvitationEmail(this.meetingId, meetingParticipant.id);
        this.toastSuccessDelay(this.$t("sendEmailInvitationSuccessful").toString());
      } catch (e) {
        this.toastError(e);
      } finally {
        this.hideLoading();
      }
    }
  }


  /*async onClickSendAllSmsInvitation() {
    const modalMessage = this.meeting.publicMeeting? "Gửi SMS mời tham gia?" : "Gửi SMS mời đến tất cả những người tham gia?";
    const confirm = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'primary',
      okTitle: this.$t("yes").toString(),
      cancelTitle: this.$t("no").toString(),
    });

    if (confirm) {
      try {
        this.showLoading();
        await meetingService.sendInvitationSms(this.meetingId, 0);
        this.toastSuccessDelay(
          this.meeting.publicMeeting? "Đã gửi SMS mời tham gia" : "Đã gửi SMS mời đến tất cả những người tham gia"
        );

      } catch (e) {
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }
  }*/

  async onClickSendSingleSmsInvitation(meetingParticipant) {
    const modalMessage = this.$t("sendSmsInvitationConfirm", { phone: meetingParticipant.phone }).toString();
    const confirm = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'primary',
      okTitle: this.$t("yes").toString(),
      cancelTitle: this.$t("no").toString(),
    });

    if (confirm) {
      try {
        this.showLoading();
        await meetingService.sendInvitationSms(this.meetingId, meetingParticipant.id);
        this.toastSuccessDelay(this.$t("sendSmsInvitationSuccessful", { phone: meetingParticipant.phone }).toString());
      } catch (e) {
        this.toastError(e);
      } finally {
        this.hideLoading();
      }
    }
  }

  onClickUpdateParticipant(meetingParticipant) {
    this.participantToUpdate = meetingParticipant;
    this.$bvModal.show("update-participant-modal");
  }

  async onClickCopyPrivateJoinUrl(p) {
    await navigator.clipboard.writeText(this.formatUrl(p.privateJoinUrl));
    this.toastSuccessDelay(this.$t("copied").toString());
  }

  ///
  async exportMeetingContent() {
    await Promise.all([
      this.exportMeetingSTTContent(),
      this.exportMeetingSummaryALLUser(),
      this.exportMeetingSummaryTimeLine(),
      this.exportMeetingSummary(),
    ]);
  }

  ///Xuất nội dung cuộc họp
  async exportMeetingSTTContent() {
    if (!this.contentRoom?.length) return;
    exportService.exportMeetingContent(this.meeting, this.contentRoom);
  }

  ///Xuất tóm tắt chung (STT & Timeline)
  async exportMeetingSummary() {
    if (!this.summaryArry?.length) return;
    exportService.exportMeetingSummary(this.meeting, this.summaryArry);
  }
  /**
   * Xuất file excel danh sách người tham gia trong cuộc họp
   */
  async downloadFileParticipant() {
    await exportService.exportParticipantJoinMeeting(this.peers, this.meeting.name);
  }

  /**
   * Xuất file excel log thao tác người tham gia trong cuộc họp.
   */
  async downloadFileHistoryHandParticipant() {
    try {

      ///api log thao tác người tham gia
      this.peersExportfilelog = await meetingService.getLogParticipantsMeeting({ mid: this.meeting.internalMeetingId });
      ///service xuất file excel log thao tác
      await exportService.exportLogParticipantExcel(this.peersExportfilelog, this.meeting.name);
    }
    catch (e) {
      console.error(e);
    }
  }

  async onClickCancelMeeting() {
    const modalMessage = this.$t("cancelThisMeetingConfirm").toString();
    const confirmCancel = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'danger',
      okTitle: this.$t("yes").toString(),
      cancelTitle: this.$t("no").toString(),
    });

    if (confirmCancel) {
      try {
        this.showLoading();
        await meetingService.cancelMeeting(this.meetingId);
        this.toastSuccessDelay(this.$t("cancelMeetingSuccessful").toString());
        this.fetchMeeting();

      } catch (e) {
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }
  }

  async onClickDeleteMeeting() {
    const modalMessage = this.$t("deleteThisMeetingConfirm").toString();
    const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'danger',
      okTitle: this.$t("yes").toString(),
      cancelTitle: this.$t("no").toString(),
    });

    if (confirmDelete) {
      try {
        this.showLoading();
        await meetingService.deleteMeeting(this.meetingId);
        this.hideLoading();
        this.$router.replace("/meetings");
        this.toastSuccessDelay(this.$t("deleteMeetingSuccessful").toString());
      } catch (e) {
        this.toastError(e);
      } finally {
        this.hideLoading();
      }
    }
  }

  /**
   * Xóa recorded
   */
  async onClickDeleteRecording() {
    const h = this.$createElement;

    const modalMessage =[ h("div", [
      h("div", [ this.$t("deleteThisRecordedConfirm", { name: this.meeting.name }) as string ]),
      h("p", [ this.$t("attention") + ": ", h("em", this.$t("deleteRecordingWarning") as string) ]),
    ])];

    const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'danger',
      okTitle: this.$t('yes').toString(),
      cancelTitle: this.$t('no').toString(),
      html: true
    });

    if (confirmDelete) {
      try {
        this.showLoading();
        await meetingService.deleteRecording(this.getUserCustomerId(), this.meeting.internalMeetingId);
        this.hideLoading();
        this.fetchMeeting();
        this.toastSuccessDelay(this.$t("deleteRecordedSuccessful").toString());
      } catch (e) {
        this.toastError(e);
      } finally {
        this.hideLoading();
      }
    }
  }

  // Xóa Record theo loại(Mp4)

  async onClickDeleteRecordingFile(type: string) {
    const h = this.$createElement;
    const typeLabel = type === 'mp3' ? this.$t('recordSound') : this.$t('recordingVideo');

    const modalMessage =[ h("div", [
      h("div", {
        domProps: {
          innerHTML: this.$t("deleteThisRecordedConfirmByType", { name: this.meeting.name, type: typeLabel }) as string
        }
      }),
      h("p", [ this.$t("attention") + ": ", h("em", this.$t("deleteRecordingWarning") as string) ]),
    ])];

    const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'danger',
      okTitle: this.$t('yes').toString(),
      cancelTitle: this.$t('no').toString(),
      html: true
    });

    if (confirmDelete) {
      try {
        this.showLoading();
        await meetingService.deleteRecordingByType(this.meetingId, type);
        this.hideLoading();
        this.fetchMeeting();
        this.toastSuccessDelay(this.$t("deleteRecordedSuccessful").toString());
      } catch (e) {
        this.toastError(e);
      } finally {
        this.hideLoading();
      }
    }
  }
  /**
   * Trả về nội dung cuộc họp đã format từ this.contentRoom
   */
  get formattedContentRoom() {
    return util.convertTitleContentMeeting(this.contentRoom, true);
  }
  /**
   * gọi api lấy tóm tắt theo timeline
   */
  async fetchMeetingSummaryByTimeline() {
    try {
      const res = await meetingService.getSummaryTimeLine({ mid: this.meeting.internalMeetingId });
      this.summaryByTimeline = res || [];
    }
    catch (e) {
       console.error(e);
    }
  }
  /**
   * Tóm tắt theo người phát biểu
   */
  formatSummary(text) {
    if (!text) return "";
    return util.markdownToHtml(text);
  }
  /**
   * Định dạng nội dung tóm tắt theo timeline
   * @param {string} item - Nội dung gốc (raw text)
   */
  get formattedTimelineList() {
    return this.summaryByTimeline.map(item =>
      util.formattedSummaryTimeLineMeeting(item)
    );
  }
  /**
   * Xuất file tóm tắt theo timeline
   */
  async exportMeetingSummaryTimeLine() {
    await exportService.exportMeetingSummaryTimeLine (
      this.meeting,
      this.summaryByTimeline,
    );
  }
  /**
   * Hiển thị tiêu đề tóm tắt theo loại
   * @param {number} index
   */
  getSummaryType(index) {
    return util.SUMMARY_TYPE_LABELS[index] || `${this.$t('summary')} ${index + 1}`;
  }

  /// Kiểm tra FaceId
  checkAuthFaceIdFeature(): boolean {
    return this.meetingFeatures?.some(
      f => f.featureCode === featureCode.AUTH_FACEID && f.isActive === Status.ENABLE
    ) ?? false;
  }

  /// Kiểm tra tính năng Ghi hình
  checkRecordingFeature(): boolean {
    const isAllowRecordFeatureEnabled = this.meetingFeatures?.some(
      f =>
        f.featureCode === featureCode.ALLOW_RECORD &&
        (f.isActive === Status.ENABLE || f.isActive === true || f.isDefault === true)
    ) ?? false;

    const isMeetingRecordingEnabled =
      this.meeting?.enableMeetingRecording === true ||
      this.meeting?.enableAutomaticMeetingRecording === true ||
      this.meeting?.usedRecording === 1;

    return isAllowRecordFeatureEnabled || isMeetingRecordingEnabled;
  }

  /// Kiểm tra tính năng AI
  checkAIFeature(): boolean {
    return this.meetingFeatures?.some(
      f => (f.featureCode === featureCode.ALLOW_SUMMARY ||
            f.featureCode === featureCode.ALLOW_LIVECAPTION) &&
           (f.isActive === Status.ENABLE || f.isActive === true || f.isDefault === true)
    ) ?? false;
  }
   /**
    * Xuất file tóm tắt theo người phát biểu
    */
  async exportMeetingSummaryALLUser() {
    await exportService.exportAllUserSummary (this.meeting, this.summaryAllUser);
  }

  /**
   * Tải tài liệu tương ứng với tab nội dung đang active
   */
  async downloadActiveTabDoc() {
    if (this.activeContentTab === 'general') {
      await this.exportMeetingSummary();
    } else if (this.activeContentTab === 'timeline') {
      await this.exportMeetingSummaryTimeLine();
    } else if (this.activeContentTab === 'speaker') {
      await this.exportMeetingSummaryALLUser();
    } else if (this.activeContentTab === 'content') {
      await this.exportMeetingSTTContent();
    }
  }

  ///
  hasMeetingContent(): boolean {
    const hasSummary = this.summaryArry?.length > 0;
    const hasContent = this.contentRoom && this.contentRoom.trim() !== '';
    const hasTimeline = this.summaryByTimeline?.length > 0;
    const hasUserSummary = this.summaryAllUser?.length > 0;
    return hasSummary || hasContent || hasTimeline || hasUserSummary;
  }
   /**
   * Chuyển đổi byte sang MB
   * @param {number} value - Giá trị byte cần chuyển đổi
   */
  convertToMB(value) {
    return util.bytesToMB(value, true, 2);
  }
}

</script>

<style lang="scss" scoped>
.selected-whiteboard-pill {
  border: 1px solid #28479B;
  border-radius: 8px;
  overflow: hidden;
  height: 32px;
  background-color: #EBEBEB;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
}

.pill-info-section {
  padding-left: 8px;
  padding-right: 12px;
  height: 100%;
  display: flex;
  align-items: center;
  color: #4b5563;

  .whiteboard-link-text {
    text-decoration: underline;
    color: #28479B;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 130px;
    display: inline-block;
  }

  &:hover {
    background-color: #dfdfdf;
  }
}

.btn-other-info {
  img {
    transition: filter 0.2s ease;
  }

  &:hover img {
    filter: brightness(0) invert(1);
  }
}
</style>
